import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEye, FiHeart, FiUsers, FiStar, FiBook, FiArrowRight } = FiIcons;

const BookVision = ({ data = {}, onUpdate, onComplete }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    bookType: '',
    why: '',
    audience: '',
    transformation: '',
    coreVerse: '',
    ...data
  });

  const [hasChanges, setHasChanges] = useState(false);

  const bookTypes = [
    'Fiction',
    'Devotional',
    'Bible Study',
    'Christian Living',
    'Memoir',
    'Other'
  ];

  useEffect(() => {
    if (hasChanges) {
      const timer = setTimeout(() => {
        if (onUpdate) {
          onUpdate(formData);
        }
        setHasChanges(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formData, hasChanges, onUpdate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const isComplete = formData.title && formData.bookType && formData.why && formData.audience && formData.transformation;

  useEffect(() => {
    if (isComplete && onComplete) {
      onComplete();
    }
  }, [isComplete, onComplete]);

  const handleContinue = () => {
    if (onComplete) {
      onComplete();
    }
    navigate('/structure');
  };

  const generatePreview = () => {
    if (!formData.title || !formData.bookType || !formData.audience || !formData.transformation) {
      return "Complete the fields above to see your book vision preview...";
    }

    const verseText = formData.coreVerse ? `, rooted in ${formData.coreVerse}` : '';
    return `"${formData.title}" is a ${formData.bookType.toLowerCase()} book for ${formData.audience}, written to help them ${formData.transformation}${verseText}.`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-8"
    >
      <div className="text-center mb-8">
        <SafeIcon icon={FiEye} className="text-4xl text-brand-orange mx-auto mb-4" />
        <h1 className="font-serif text-3xl text-stone-800 mb-2">Book Vision</h1>
        <p className="text-stone-600">Define your purpose, audience, and divine calling</p>
      </div>

      <div className="grid gap-6">
        {/* Book Title */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <label className="block text-stone-800 font-medium mb-3">
            <SafeIcon icon={FiBook} className="inline mr-2" />
            Book Title
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="What will you call this God-given project?"
            className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 outline-none transition-all"
          />
        </div>

        {/* Book Type */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <label className="block text-stone-800 font-medium mb-3">
            <SafeIcon icon={FiStar} className="inline mr-2" />
            Book Type
          </label>
          <select
            value={formData.bookType}
            onChange={(e) => handleChange('bookType', e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all"
          >
            <option value="">Select your book type...</option>
            {bookTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        {/* God-given Why */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <label className="block text-stone-800 font-medium mb-3">
            <SafeIcon icon={FiHeart} className="inline mr-2" />
            What's your God-given why for writing this book?
          </label>
          <textarea
            value={formData.why}
            onChange={(e) => handleChange('why', e.target.value)}
            placeholder="What burden, calling, or message has God placed on your heart?"
            rows={4}
            className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 outline-none transition-all resize-none"
          />
        </div>

        {/* Audience */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <label className="block text-stone-800 font-medium mb-3">
            <SafeIcon icon={FiUsers} className="inline mr-2" />
            Who is this book for?
          </label>
          <textarea
            value={formData.audience}
            onChange={(e) => handleChange('audience', e.target.value)}
            placeholder="Describe the readers God has called you to serve..."
            rows={3}
            className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all resize-none"
          />
        </div>

        {/* Transformation */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <label className="block text-stone-800 font-medium mb-3">
            What's the main transformation or spiritual outcome?
          </label>
          <textarea
            value={formData.transformation}
            onChange={(e) => handleChange('transformation', e.target.value)}
            placeholder="How will readers be different after reading your book?"
            rows={3}
            className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 outline-none transition-all resize-none"
          />
        </div>

        {/* Core Verse */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <label className="block text-stone-800 font-medium mb-3">
            Core Verse (Optional)
          </label>
          <input
            type="text"
            value={formData.coreVerse}
            onChange={(e) => handleChange('coreVerse', e.target.value)}
            placeholder="e.g., Jeremiah 29:11 or Romans 8:28"
            className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all"
          />
        </div>
      </div>

      {/* Preview */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-gradient-to-r from-brand-orange/10 to-brand-blue/10 rounded-xl p-6 border border-brand-orange/20"
      >
        <h3 className="font-serif text-xl text-stone-800 mb-4">Your Book Vision Preview</h3>
        <p className="text-stone-700 italic text-lg leading-relaxed">
          {generatePreview()}
        </p>
      </motion.div>

      {/* Progress Indicator */}
      {isComplete && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-4"
        >
          <div className="inline-flex items-center bg-brand-blue/10 text-brand-blue px-4 py-2 rounded-full mb-6">
            <SafeIcon icon={FiHeart} className="mr-2" />
            Vision Complete! You're stewarding your gift well.
          </div>
        </motion.div>
      )}

      {/* Continue Button */}
      {isComplete && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-end"
        >
          <button
            onClick={handleContinue}
            className="bg-gradient-to-r from-brand-orange to-brand-blue text-white px-8 py-3 rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center"
          >
            Continue to Structure & Outline
            <SafeIcon icon={FiArrowRight} className="ml-2" />
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default BookVision;